<template>
  <z-dropdown
    v-if="initStore.contextMenu"
    ref="dropdownRef"
    :virtual-ref="triggerRef"
    virtual-triggering
    trigger="click"
    placement="bottom-start"
    :show-arrow="false"
    :hide-on-click="false"
    :popper-options="popperOptions"
    :popper-class="ns.e('context-menu')"
    persistent
    @command="onCommand"
    @visible-change="onVisibleChange"
  >
    <template #dropdown>
      <z-dropdown-menu>
        <z-dropdown-item
          v-for="item in items"
          :key="item.code"
          :command="item"
          :disabled="item.disabled"
          :divided="item.divided"
          :icon="item.icon"
          :class="ns.is('opened', submenuCode === item.code)"
          @pointermove="onItemPointerMove(item, $event)"
        >
          <span :class="ns.e('context-menu-item')">
            <span>{{ item.label }}</span>
            <z-icon
              v-if="item.children?.length"
              :class="ns.e('context-menu-arrow')"
            >
              <ArrowRight />
            </z-icon>
          </span>
        </z-dropdown-item>
      </z-dropdown-menu>
    </template>
  </z-dropdown>

  <z-dropdown
    v-if="initStore.contextMenu"
    ref="submenuRef"
    :virtual-ref="submenuTriggerRef"
    virtual-triggering
    trigger="hover"
    placement="right-start"
    :show-arrow="false"
    :hide-on-click="true"
    :show-timeout="0"
    :hide-timeout="180"
    :max-height="320"
    :popper-options="submenuPopperOptions"
    :popper-class="ns.e('context-menu-sub')"
    persistent
    @command="onCommand"
    @visible-change="onSubmenuVisibleChange"
  >
    <template #dropdown>
      <z-dropdown-menu
        @pointerenter="onSubmenuEnter"
        @pointerleave="scheduleCloseSubmenu"
      >
        <z-dropdown-item
          v-for="child in submenuChildren"
          :key="child.code"
          :command="child"
          :disabled="child.disabled"
          :divided="child.divided"
          :icon="child.icon"
        >
          {{ child.label }}
        </z-dropdown-item>
      </z-dropdown-menu>
    </template>
  </z-dropdown>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, watch } from 'vue'
import { ArrowRight } from '@zoryn/icons'
import { useNamespace } from '@zoryn/hooks'
import ZDropdown, {
  ZDropdownItem,
  ZDropdownMenu,
} from '@zoryn/components/dropdown'
import ZIcon from '@zoryn/components/icon'
import { resolveContextMenuItems } from '../methods/row-context-menu'
import type { DropdownInstance } from '@zoryn/components/dropdown'
import type { DataGrid, GridContextMenuItem } from '../types'

const ns = useNamespace('grid')
const $grid = inject('$grid') as DataGrid
const dropdownRef = ref<DropdownInstance>()
const submenuRef = ref<DropdownInstance>()
const submenuItem = ref<GridContextMenuItem | null>(null)
const submenuAnchor = ref<DOMRect | null>(null)
let submenuLeaveTimer: ReturnType<typeof setTimeout> | null = null

const store = computed(() => $grid.reactData.contextMenuStore)
const initStore = computed(() => $grid.reactData.initStore)
const submenuCode = computed(() => submenuItem.value?.code ?? '')
const submenuChildren = computed(() => submenuItem.value?.children ?? [])

const items = computed(() => {
  if (!store.value.visible && !store.value.row) {
    return []
  }
  return resolveContextMenuItems($grid)
})

function cursorRect(x: number, y: number, width = 0, height = 0): DOMRect {
  return {
    x,
    y,
    width,
    height,
    top: y,
    left: x,
    right: x + width,
    bottom: y + height,
    toJSON: () => ({}),
  } as DOMRect
}

const triggerRef = computed(() => ({
  getBoundingClientRect: () => cursorRect(store.value.x, store.value.y),
}))

const submenuTriggerRef = computed(() => ({
  getBoundingClientRect: () =>
    submenuAnchor.value
    ?? cursorRect(store.value.x, store.value.y),
}))

const popperOptions = {
  modifiers: [{ name: 'offset', options: { offset: [0, 0] } }],
}

const submenuPopperOptions = {
  modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
}

function clearSubmenuTimer() {
  if (submenuLeaveTimer) {
    clearTimeout(submenuLeaveTimer)
    submenuLeaveTimer = null
  }
}

function closeSubmenu() {
  clearSubmenuTimer()
  submenuItem.value = null
  submenuAnchor.value = null
  submenuRef.value?.handleClose()
}

function scheduleCloseSubmenu() {
  clearSubmenuTimer()
  submenuLeaveTimer = setTimeout(() => {
    closeSubmenu()
  }, 180)
}

function onSubmenuEnter() {
  clearSubmenuTimer()
}

async function openSubmenu(item: GridContextMenuItem, target: EventTarget | null) {
  clearSubmenuTimer()
  if (!(target instanceof HTMLElement)) {
    return
  }
  submenuAnchor.value = target.getBoundingClientRect()
  submenuItem.value = item
  await nextTick()
  submenuRef.value?.handleOpen()
}

function onItemPointerMove(item: GridContextMenuItem, event: PointerEvent) {
  if (item.disabled) {
    return
  }
  if (item.children?.length) {
    if (submenuCode.value === item.code) {
      clearSubmenuTimer()
      return
    }
    void openSubmenu(item, event.currentTarget)
    return
  }
  if (submenuItem.value) {
    scheduleCloseSubmenu()
  }
}

async function syncDropdownVisible(visible: boolean) {
  await nextTick()
  if (visible) {
    dropdownRef.value?.handleOpen()
  } else {
    closeSubmenu()
    dropdownRef.value?.handleClose()
  }
}

watch(
  () => store.value.visible,
  (visible) => {
    void syncDropdownVisible(visible)
  },
  { flush: 'post', immediate: true }
)

watch(
  () => $grid.reactData.currentPage,
  () => {
    if (store.value.visible) {
      $grid.closeContextMenu?.()
    }
  }
)

watch(
  () => $grid.reactData.tableData,
  () => {
    if (store.value.visible) {
      $grid.closeContextMenu?.()
    }
  }
)

function onVisibleChange(visible: boolean) {
  if (!visible) {
    closeSubmenu()
    if (store.value.visible) {
      $grid.closeContextMenu?.()
    }
  }
}

function onSubmenuVisibleChange(visible: boolean) {
  if (!visible && submenuItem.value) {
    scheduleCloseSubmenu()
  }
}

function onCommand(command: string | number | object) {
  if (!command || typeof command !== 'object' || !('code' in command)) {
    return
  }
  const item = command as GridContextMenuItem
  if (item.children?.length) {
    return
  }
  $grid.handleContextMenuCommand?.(item)
}
</script>
